import * as React from "react";
import {
  autocompleteClasses,
  AutocompleteGetTagProps,
  Box,
  Chip,
  createFilterOptions,
  FormControl,
  styled,
  TextField,
  TextFieldProps
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { Field, useFormikContext } from "formik";
import { Autocomplete, AutocompleteRenderInputParams } from "formik-mui";
import { useTranslation } from "react-i18next";
import { SelectOption } from "@mui/base";
import { ListboxComponent, StyledPopper } from "./ListBoxComponent";

interface LocalProps {
  name: string;
  label: string;
  options: SelectOption<string>[];
  required?: boolean;
  requiredMessage?: string;
  isLoading?: boolean;
  isError?: boolean;
}

type Props = TextFieldProps & LocalProps;

const filterOptions = createFilterOptions({
  matchFrom: "start",
  trim: true
});

const visuallyHiddenSx = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0
} as const;

const ChipDeleteButton = styled("button")({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  border: "none",
  borderRadius: "50%",
  background: "none",
  "& svg": {
    fontSize: "inherit"
  },
  "&:focus-visible": {
    outline: "2px solid currentColor",
    outlineOffset: 1
  }
});

const SelectManyAutocompleteField: React.FC<Props> = ({
  name,
  label,
  options,
  required,
  requiredMessage,
  disabled,
  isLoading,
  isError,
  variant = "standard",
  ...others
}) => {
  const { t } = useTranslation();
  const requiredErrorMessage = requiredMessage || t("common.validation.required");
  const { values, errors, touched, isSubmitting } = useFormikContext<Record<string, string>>();
  const selectedOptions = values[name] as unknown as SelectOption<string>[] | undefined;
  const selectedLabels = selectedOptions?.map(option => option.label).join(", ");
  const [highlightedValue, setHighlightedValue] = React.useState<string>();
  const formControlRef = React.useRef<HTMLDivElement>(null);

  const onHighlightChange = React.useCallback(
    (_event: React.SyntheticEvent, option: SelectOption<string> | null) => setHighlightedValue(option?.value),
    []
  );

  const validate = React.useCallback(
    (value: SelectOption<string>[] | null) =>
      required && (!value || value.length === 0) ? requiredErrorMessage : undefined,
    [required, requiredErrorMessage]
  );

  const isOptionEqualToValue = React.useCallback(
    (option: SelectOption<string>, value: string | SelectOption<string>) => {
      return option === value || option.value === value || option.value === (value as SelectOption<string>).value;
    },
    []
  );

  const renderInput = React.useCallback(
    (params: AutocompleteRenderInputParams) => {
      const helperText = (touched[name] && (errors[name] as string)) || others.helperText;
      const selectedValuesId = `${params.id}-selected-values`;
      const describedBy = [
        selectedLabels ? selectedValuesId : undefined,
        helperText ? `${params.id}-helper-text` : undefined
      ]
        .filter(Boolean)
        .join(" ");
      return (
        <>
          <TextField
            {...params}
            inputProps={{ ...params.inputProps, "aria-describedby": describedBy || undefined }}
            // We have to manually set the corresponding fields on the input component
            name={name}
            error={(touched[name] && !!(errors[name] as string)) || isError}
            helperText={helperText}
            label={label}
            variant={variant}
            required={required}
            disabled={isSubmitting}
          />
          {selectedLabels && (
            <Box component="span" id={selectedValuesId} sx={visuallyHiddenSx}>
              {t("common.selectedItems", { items: selectedLabels })}
            </Box>
          )}
        </>
      );
    },
    [name, touched, errors, isError, others.helperText, label, variant, required, isSubmitting, selectedLabels, t]
  );

  const renderTags = React.useCallback(
    (value: SelectOption<string>[], getTagProps: AutocompleteGetTagProps) =>
      value.map((option, index) => {
        const {
          key,
          onDelete,
          disabled: isTagDisabled,
          ...tagProps
        } = getTagProps({ index }) as ReturnType<AutocompleteGetTagProps> & { key: React.Key; disabled: boolean };
        const onDeleteTag = (event: React.SyntheticEvent) => {
          onDelete(event);
          formControlRef.current?.querySelector("input")?.focus();
        };
        return (
          <Chip
            key={key}
            {...tagProps}
            role={undefined}
            disabled={isTagDisabled}
            label={option.label}
            onDelete={onDeleteTag}
            deleteIcon={
              <ChipDeleteButton
                type="button"
                disabled={isTagDisabled}
                aria-label={t("common.removeItem", { item: option.label })}
              >
                <CancelIcon aria-hidden={true} />
              </ChipDeleteButton>
            }
          />
        );
      }),
    [t]
  );

  return (
    <FormControl fullWidth ref={formControlRef}>
      <Field
        {...others}
        component={Autocomplete}
        type="text"
        name={name}
        disableListWrap
        renderInput={renderInput}
        placeholder={isLoading ? "Loading..." : (isError && "Error") || ""}
        PopperComponent={StyledPopper}
        ListboxComponent={ListboxComponent}
        ListboxProps={{ "aria-multiselectable": true, highlightedValue }}
        onHighlightChange={onHighlightChange}
        options={options}
        renderOption={(props: unknown, option: SelectOption<string>, { selected }: { selected: boolean }) =>
          [props, { selected, label: option.label, value: option.value }] as React.ReactNode
        }
        disabled={disabled || isLoading || isError || isSubmitting}
        required={required}
        validate={validate}
        isOptionEqualToValue={isOptionEqualToValue}
        multiple={true}
        disableCloseOnSelect={true}
        filterOptions={filterOptions}
        renderTags={renderTags}
        clearText={t("common.clearAll")}
        componentsProps={{
          clearIndicator: {
            tabIndex: 0,
            onClickCapture: () => formControlRef.current?.querySelector("input")?.focus()
          }
        }}
        sx={{
          [`&:focus-within .${autocompleteClasses.clearIndicator}`]: {
            visibility: "visible"
          }
        }}
      />
    </FormControl>
  );
};

export { SelectManyAutocompleteField };
